import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { server } from "../server";
import {
  User,
  Lock,
  Phone,
  Briefcase,
  Building2,
  Shield,
  Plus,
  Mail,
  Loader2,
  X,
  CheckCircle2,
} from "lucide-react";
import useAuth from "../useAuth";
import { policyOptions } from "../jsons/policyOptions";

export default function RegisterUser() {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    password: "",
    fullName: "",
    mobileNo: "",
    email: "",
    designation: "",
    userType: "",
    vlanId: "20",
    receivedDepartment: "Admin",
    policy: "",
    isReadOnly: false,
    isVerified: true,
    departmentId: null,
  });

  const [selectedDepartment, setSelectedDepartment] = useState("");
  const [departmentsList, setDepartmentsList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [isAcknowledged, setIsAcknowledged] = useState(false);

  // Fetch Departments
  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const token = localStorage.getItem("dbToken");
        const response = await axios.get(`${server}/department/getAll`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          params: {
            pageNo: 1,
            pageSize: 1000,
            userWise: user?.userType === "admin" ? "false" : "true"
          },
        });
        if (response.data.success) {
          setDepartmentsList(response.data.data);
        }
      } catch (error) {
        console.error("Error fetching departments:", error);
        toast.error("Failed to load departments.");
      }
    };
    fetchDepartments();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsAcknowledged(false); // Reset acknowledgement when opening preview
    setShowPreview(true);
  };


  const handleConfirmSubmit = async () => {
    if (!isAcknowledged) return; // Safety check

    setLoading(true);
    setShowPreview(false);
    setIsAcknowledged(false); // Reset for next time

    const payload = {
      username: String(formData.mobileNo),
      password: formData.password,
      fullName: formData.fullName,
      phoneNumber: String(formData.mobileNo),
      email: formData.email,
      department: formData.receivedDepartment,
      policiesMapperId: policyOptions?.find((e) => e.userType.toLocaleLowerCase() === user?.userType.toLocaleLowerCase())?.value,
      isVerified: formData.isVerified,
      departmentId: formData.departmentId,
    };

    try {
      const token = localStorage.getItem("dbToken");
      const response = await axios.post(`${server}/wifiusers/new`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.status === 200 || response.status === 201) {
        // Get the newly created user ID and approve it
        const newUserId = response.data?.data?.id;

        if (newUserId) {
          try {
            await axios.patch(
              `${server}/wifiusers/approveUser?wifiUserId=${newUserId}`,
              {},
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                  accept: "*/*",
                },
              }
            );
            toast.success("User Registration and Approval Successful!");
          } catch (approveError) {
            console.error("Approval Error:", approveError);
            toast.warning("User registered but approval failed. Please approve manually.");
          }
        } else {
          toast.success("User Registration Successful!");
        }

        setFormData({
          password: "",
          fullName: "",
          mobileNo: "",
          email: "",
          designation: "",
          userType: "",
          vlanId: "20",
          receivedDepartment: "Admin",
          policy: "",
          isReadOnly: false,
          isVerified: true,
          departmentId: null,
        });
        setSelectedDepartment("");
      } else {
        toast.error("Registration failed. Please try again.");
      }
    } catch (error) {
      console.error("Registration Error:", error);
      toast.error(
        error.response?.data?.message || "An error occurred during registration."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto relative">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 tracking-tight">
          WiFi Registration
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Register to get WiFi credentials
        </p>
      </div>

      <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-gray-100 p-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Full Name */}
            <div className="space-y-2">
              <label className="text-xs font-bold text-gray-500 uppercase tracking-wider block">
                Full Name
              </label>
              <div className="relative group">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleChange}
                  placeholder="Enter full name"
                  className="w-full pl-9 pr-4 py-2.5 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 transition-all placeholder-gray-400"
                  required
                />
              </div>
            </div>

            {/* Mobile No */}
            <div className="space-y-2">
              <label className="text-xs font-bold text-gray-500 uppercase tracking-wider block">
                Mobile No / Username
              </label>
              <div className="relative group">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="number"
                  name="mobileNo"
                  value={formData.mobileNo}
                  onChange={handleChange}
                  placeholder="Enter mobile no"
                  className="w-full pl-9 pr-4 py-2.5 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 transition-all placeholder-gray-400"
                  required
                  maxLength={10}
                  onInput={(e) => {
                    if (e.target.value.length > 10)
                      e.target.value = e.target.value.slice(0, 10);
                  }}
                />
              </div>
            </div>

            {/* Email */}
            <div className="space-y-2">
              <label className="text-xs font-bold text-gray-500 uppercase tracking-wider block">
                Email
              </label>
              <div className="relative group">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Enter email"
                  className="w-full pl-9 pr-4 py-2.5 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 transition-all placeholder-gray-400"
                  required
                />
              </div>
            </div>

            {/* Password */}
            <div className="space-y-2">
              <label className="text-xs font-bold text-gray-500 uppercase tracking-wider block">
                Password
              </label>
              <div className="relative group">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Create a password"
                  className="w-full pl-9 pr-4 py-2.5 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 transition-all placeholder-gray-400"
                  required
                />
              </div>
            </div>

            {/* Designation */}
            <div className="space-y-2">
              <label className="text-xs font-bold text-gray-500 uppercase tracking-wider block">
                Designation
              </label>
              <div className="relative group">
                <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  name="designation"
                  value={formData.designation}
                  onChange={handleChange}
                  placeholder="Designation"
                  className="w-full pl-9 pr-4 py-2.5 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 transition-all placeholder-gray-400"
                />
              </div>
            </div>

            {/* User Type */}
            {/* <div className="space-y-2">
              <label className="text-xs font-bold text-gray-500 uppercase tracking-wider block">
                User Type
              </label>
              <div className="relative group">
                <Shield className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <select
                  name="userType"
                  value={formData.userType}
                  onChange={handleUserTypeChange}
                  className="w-full pl-9 pr-4 py-2.5 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 transition-all cursor-pointer appearance-none"
                  required
                >
                  <option value="" disabled>
                    Select Type
                  </option>
                  {userTypes.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>
            </div> */}

            {/* Department Selection */}
            {/* <div className="space-y-2 md:col-span-2 bg-blue-50 p-4 rounded-xl border border-blue-100">
              <label className="text-xs font-bold text-blue-700 uppercase tracking-wider block">
                Select Department
              </label>
              <div className="relative group mt-2">
                <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-blue-400" />
                <select
                  value={selectedDepartment}
                  onChange={handleDepartmentChange}
                  className="w-full pl-9 pr-4 py-2.5 bg-white border border-blue-200 rounded-lg text-sm font-medium text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400 transition-all cursor-pointer appearance-none"
                  required
                >
                  <option value="" disabled>
                    Select Department
                  </option>
                  {departmentsList.map((dept, index) => (
                    <option key={index} value={dept.departmentName}>
                      {dept.departmentName}
                    </option>
                  ))}
                </select>
              </div>
            </div> */}
          </div>

          <button
            type="submit"
            disabled={loading}
            className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-lg font-semibold text-white transition-all shadow-sm ${loading
              ? "bg-gray-100 text-gray-400 cursor-not-allowed shadow-none"
              : "bg-blue-600 hover:bg-blue-700 active:bg-blue-800 hover:shadow"
              }`}
          >
            {loading ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                <span>Registering...</span>
              </>
            ) : (
              <>
                <Plus className="h-5 w-5" />
                <span>Register</span>
              </>
            )}
          </button>
        </form>
      </div>

      {/* Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/60 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white w-full max-w-lg rounded-2xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-gray-50/50">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-blue-600" />
                <h3 className="text-lg font-bold text-gray-900">Review Registration</h3>
              </div>
              <button
                onClick={() => setShowPreview(false)}
                className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 p-1 rounded-full transition-all"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-4">
              <p className="text-sm font-medium text-gray-600 mb-4 bg-blue-50 p-3 rounded-lg border border-blue-100">
                Please confirm the following details before submitting. Once submitted, these details will be used to create the WiFi account.
              </p>

              <div className="grid grid-cols-2 gap-y-4 gap-x-6">
                <div>
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-0.5">Full Name</label>
                  <p className="text-sm font-semibold text-gray-900">{formData.fullName}</p>
                </div>
                <div>
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-0.5">Mobile No</label>
                  <p className="text-sm font-semibold text-gray-900">{formData.mobileNo}</p>
                </div>
                <div className="col-span-2">
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-0.5">Email</label>
                  <p className="text-sm font-semibold text-gray-900">{formData.email}</p>
                </div>
                {/* <div>
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-0.5">User Type</label>
                  <p className="text-sm font-semibold text-gray-900">{formData.userType}</p>
                </div> */}
                <div>
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-0.5">Designation</label>
                  <p className="text-sm font-semibold text-gray-900">{formData.designation || "-"}</p>
                </div>
                {/* <div className="col-span-2">
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-0.5">Department</label>
                  <p className="text-sm font-semibold text-gray-900">{formData.receivedDepartment}</p>
                </div> */}
                {/* <div className="col-span-2">
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-0.5">Policy</label>
                  <p className="text-sm font-semibold text-gray-900">{formData.policy}</p>
                </div> */}
              </div>

              <div className="pt-4 space-y-4">
                <div
                  className="flex items-start gap-3 p-3 bg-amber-50 rounded-xl border border-amber-100 cursor-pointer group hover:bg-amber-100 transition-colors"
                  onClick={() => setIsAcknowledged(!isAcknowledged)}
                >
                  <div className={`mt-0.5 w-5 h-5 rounded border flex items-center justify-center shrink-0 transition-all ${isAcknowledged ? 'bg-blue-600 border-blue-600' : 'bg-white border-gray-300 group-hover:border-blue-400'}`}>
                    {isAcknowledged && <span className="text-white text-[10px] font-bold">✓</span>}
                  </div>
                  <p className="text-xs font-semibold text-gray-700 select-none">
                    I confirm that all the details provided above are correct and accurate to the best of my knowledge.
                  </p>
                </div>

                <div className="flex flex-col items-center gap-3 pt-2">
                  <div className="flex w-full gap-3">
                    <button
                      onClick={() => {
                        setShowPreview(false);
                        setIsAcknowledged(false);
                      }}
                      className="flex-1 px-4 py-2.5 rounded-xl text-sm font-bold text-gray-600 bg-gray-100 hover:bg-gray-200 transition-all border border-gray-200"
                    >
                      No, Go Back
                    </button>
                    <button
                      onClick={handleConfirmSubmit}
                      disabled={loading || !isAcknowledged}
                      className={`flex-1 px-4 py-2.5 rounded-xl text-sm font-bold text-white transition-all shadow-md ${loading || !isAcknowledged ? 'bg-gray-300 cursor-not-allowed shadow-none' : 'bg-blue-600 hover:bg-blue-700 shadow-blue-200'}`}
                    >
                      Confirm & Submit
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
